import { type FetcherWithComponents, json } from '@remix-run/react';

export function isFetcherTypeActionReload(fetcher: FetcherWithComponents<unknown>): boolean {
  return (
    fetcher.state === 'loading' &&
    fetcher.formMethod !== null &&
    fetcher.formMethod !== 'GET' &&
    // If we returned data, we must be reloading
    fetcher.data !== null
  );
}

export function hasKey<T extends string>(obj: unknown, key: T): obj is Record<T, unknown> {
  return typeof obj === 'object' && obj !== null && key in obj;
}
export function isObjectWithErrors(obj: unknown): obj is { errors: string[] } {
  return hasKey(obj, 'errors') && Array.isArray(obj.errors);
}

export function getFetcherResult<TData>({ state, data }: Pick<FetcherWithComponents<TData>, 'state' | 'data'>) {
  if (state !== 'idle') {
    return { ok: false, error: '' };
  }

  // we can't use !!data here because data could be falsey values like ''
  const hasData = data !== null && data !== undefined;
  const hasErrors = hasData && hasKey(data, 'errors');
  if (hasErrors) {
    const handled = handleErrors(data.errors);
    if (handled) {
      return handled;
    }
  }

  if (hasData && hasKey(data, 'error')) {
    if (hasKey(data.error, 'errors')) {
      const handled = handleErrors(data.error.errors);
      if (handled) {
        return handled;
      }
    }

    const message =
      hasKey(data.error, 'message') && data.error.message
        ? data.error.message
        : typeof data.error === 'string' && data.error
          ? data.error
          : 'unknown error';
    return { ok: false, error: message };
  }
  // nullish data without errors means this is very first load
  return { ok: hasData, error: '' };
}

export type FetcherResult<TData = any> = ReturnType<typeof getFetcherResult<TData>>;

// ideally we use this function in our BFF to adopt the error response, so we only have 2 cases of errors:
// 1. { errors: string[] } for array of errors
// 2. { errors: Record<string, string> } for field level errors
export async function adoptErrorResponse(response: Response) {
  const { message, errors } = (await response.json()) as {
    message?: string;
    errors?: Record<string, string> | string[];
  };
  return json({ errors: errors ?? [message || 'unknown error'] }, { status: response.status });
}

function handleErrors(errors: unknown) {
  if (Array.isArray(errors)) {
    return { ok: false, error: errors[0] ?? 'unknown error' };
  } else if (errors && typeof errors === 'object' && Object.keys(errors).length > 0) {
    // field level errors.
    return { ok: false, error: errors };
  }
}
